import type { ExploraPayload } from '../types/payloads/explora.types';
import type { InflationMarketPayload, SandboxPayload } from '../types/payloads/sandbox.types';
import type { SimulationMatrixPayload } from './mathBlocks';
import { validateSimulationMatrixPayload } from './mathBlocks';
import { enrichSimulationMatrixForSandbox } from '../print/sandboxMatrixEnrich';

function hidden(placeholderLabel?: string) {
  if (placeholderLabel && !/^(efecto|notas|factor|operaci|valor|resultado|sí\/no)$/i.test(placeholderLabel.trim())) {
    return { value: '', isHiddenInput: true as const, placeholderLabel };
  }
  return { value: '', isHiddenInput: true as const };
}

function visible(value: string) {
  return { value, isHiddenInput: false as const };
}

export type SandboxPrintIntent =
  | 'percent-variation'
  | 'fraction-decimal-table'
  | 'explora-fractions'
  | 'inverse-operations'
  | 'inflation'
  | 'open-exploration'
  | 'text-composition'
  | 'numeric-reach-target';

/** Dominio de headers para exploración abierta (P1 #7 — no plantilla matemática universal). */
export type OpenExplorationDomain = 'matematica' | 'ciencias' | 'humanidades' | 'general';

const MATH_OPEN_HEADERS = Object.freeze([
  'Paso',
  'Factor o acción',
  'Efecto observado',
  'Notas / estado',
]);

const CIENCIAS_OPEN_HEADERS = Object.freeze([
  'Paso',
  'Condición inicial',
  'Fenómeno observado',
  'Notas',
]);

const HUMANIDADES_OPEN_HEADERS = Object.freeze([
  'Paso',
  'Elemento / evidencia',
  'Interpretación',
  'Notas',
]);

const GENERAL_OPEN_HEADERS = Object.freeze([
  'Paso',
  'Qué pruebo o cambio',
  'Qué observo',
  'Notas',
]);

/**
 * Resuelve dominio de plantilla abierta desde subjectTag.
 * Sin tag (p. ej. preview vieja) conserva matemática = headers históricos.
 */
export function resolveOpenExplorationDomain(subjectTag?: string | null): OpenExplorationDomain {
  const tag = String(subjectTag || '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (!tag) return 'matematica';
  if (tag === 'matematica' || tag.includes('matemat')) return 'matematica';
  if (tag === 'ciencias' || tag.includes('cienc')) return 'ciencias';
  if (
    tag === 'historia' ||
    tag.includes('histor') ||
    tag === 'lenguaje' ||
    tag.includes('lengua') ||
    tag.includes('literatura') ||
    tag === 'ingles' ||
    tag.includes('english') ||
    tag.includes('ingles')
  ) {
    return 'humanidades';
  }
  return 'general';
}

export function openExplorationHeadersForDomain(domain: OpenExplorationDomain): readonly string[] {
  switch (domain) {
    case 'ciencias':
      return CIENCIAS_OPEN_HEADERS;
    case 'humanidades':
      return HUMANIDADES_OPEN_HEADERS;
    case 'general':
      return GENERAL_OPEN_HEADERS;
    case 'matematica':
    default:
      return MATH_OPEN_HEADERS;
  }
}

function openExplorationIntroForDomain(domain: OpenExplorationDomain, unit?: string): string {
  if (domain === 'ciencias') {
    return unit
      ? `Registra la condición inicial y el fenómeno observado (unidad de referencia: «${unit}»).`
      : 'Registra la condición inicial y el fenómeno observado en la exploración.';
  }
  if (domain === 'humanidades') {
    return unit
      ? `Registra evidencia o elementos del material y cómo los interpretas («${unit}»).`
      : 'Registra evidencia o elementos del material y cómo los interpretas.';
  }
  if (domain === 'general') {
    return unit
      ? `Anota qué pruebas o cambios y qué observas («${unit}»).`
      : 'Anota qué pruebas o cambios y qué observas.';
  }
  return unit
    ? `Registra factores, decisiones o eventos y cómo influyen en «${unit}».`
    : 'Registra factores, decisiones o eventos y cómo influyen en el fenómeno que estudias.';
}

/** Headers rígidos del taller matemático genérico (señal de contaminación cross-subject). */
export function matrixHasMathWorkshopHeaders(headers: string[] = []): boolean {
  return headers.some((h) => /factor o acci[oó]n|efecto observado|notas\s*\/?\s*estado/i.test(h));
}

function isExploraPayload(payload: SandboxPayload): payload is ExploraPayload {
  return payload != null && typeof payload === 'object' && 'availableVariables' in payload;
}

function isInflationPayload(payload: SandboxPayload): payload is InflationMarketPayload {
  return payload != null && typeof payload === 'object' && payload.variant === 'inflation-market';
}

/** Combina pistas de tema desde título del bloque, label y payload. */
export function collectSandboxTopicHints(payload: SandboxPayload, topicHint = ''): string {
  const parts = [topicHint];
  if (isExploraPayload(payload) && payload.title) parts.push(payload.title);
  if (isInflationPayload(payload) && payload.title) parts.push(payload.title);
  if (isInflationPayload(payload) && payload.productName) parts.push(payload.productName);
  return parts.filter(Boolean).join(' · ');
}

function isMathOrientedExplora(payload: ExploraPayload, hints: string): boolean {
  const h = hints.toLowerCase();
  if (payload.variant === 'fracciones' || payload.skin === 'fractions') return true;
  if (payload.skin === 'algebra' || payload.balanceMode === 'inverse-operations') return true;
  if (/fracci|decimal|ecuaci|algebra|porcentaj|variaci[oó]n|inflaci/i.test(h)) return true;
  if (
    (payload.skin === 'economy' || payload.variant === 'grafico') &&
    /porcentaj|variaci[oó]n|inflaci|precio/i.test(h)
  ) {
    return true;
  }
  return false;
}

/** Humanidades, lengua, conectores — no usar matriz de «factores/eventos». */
export function topicLooksLanguage(hints: string): boolean {
  return /conector|lenguaje|lengua|texto|narrativ|escritura|oraci[oó]n|p[aá]rrafo|coherencia|fluidez|gram[aá]tica|comunicaci[oó]n|lectura|redacci[oó]n|v[ií]nculo|adversativ|causal|temporal/i.test(
    hints,
  );
}

export function inferSandboxPrintIntent(
  payload: SandboxPayload,
  topicHint = '',
): SandboxPrintIntent {
  const hints = collectSandboxTopicHints(payload, topicHint).toLowerCase();

  if (isInflationPayload(payload)) return 'inflation';

  if (isExploraPayload(payload)) {
    if (
      payload.balanceMode === 'inverse-operations' ||
      payload.skin === 'algebra' ||
      (payload.equationLeft && payload.equationRight)
    ) {
      return 'inverse-operations';
    }
    if (payload.variant === 'fracciones' || payload.skin === 'fractions') {
      return 'explora-fractions';
    }
    if (/fracci|decimal|equival/i.test(hints) && payload.variant !== 'fracciones') {
      return 'fraction-decimal-table';
    }
    if (/porcentaj|variaci[oó]n|inflaci/i.test(hints)) {
      return 'percent-variation';
    }
    if (isMathOrientedExplora(payload, hints)) {
      if (payload.skin === 'economy' || payload.variant === 'grafico') {
        return 'percent-variation';
      }
      return 'numeric-reach-target';
    }
  }

  if (/porcentaj|variaci[oó]n|inflaci/i.test(hints)) return 'percent-variation';
  if (/fracci|decimal|equival/i.test(hints)) return 'fraction-decimal-table';
  if (topicLooksLanguage(hints)) return 'text-composition';

  return 'open-exploration';
}

export function buildTextCompositionMatrix(payload: ExploraPayload, topicHint = ''): SimulationMatrixPayload {
  const topic = collectSandboxTopicHints(payload, topicHint).slice(0, 96) || payload.title || 'Conectores';
  const defaultConnectors = [
    'y (adición / suma de ideas)',
    'pero (contraste)',
    'porque (causa)',
    'entonces (consecuencia)',
    'además (amplía información)',
  ];
  const seedVars = payload.availableVariables.slice(0, 5);
  const labels =
    seedVars.length >= 2
      ? seedVars.map((v) => String(v.label || '').trim()).filter(Boolean)
      : defaultConnectors;

  return {
    layoutType: 'case_comparison',
    title: payload.title,
    instructionText:
      `Construye oraciones enlazadas con conectores. Parte de una idea central y usa cada conector de la tabla para unir oraciones relacionadas. Tema: ${topic}.`.trim(),
    headers: ['Paso', 'Conector', 'Oración que lo usa', '¿Qué relación une?'],
    rows: labels.map((label, index) => ({
      id: `row_${index + 1}`,
      cells: [visible(String(index + 1)), visible(label), hidden(), hidden()],
    })),
    workspace: { gridType: 'blank', heightRows: 6 },
  };
}

export function buildOpenExplorationMatrix(
  payload: ExploraPayload,
  subjectTag?: string | null,
): SimulationMatrixPayload {
  const domain = resolveOpenExplorationDomain(subjectTag);
  const unit = payload.unitLabel?.trim();
  const intro = openExplorationIntroForDomain(domain, unit || undefined);

  const seedVars = payload.availableVariables.slice(0, 4);
  const rows =
    seedVars.length >= 2
      ? seedVars.map((v, i) => ({
          id: `row_${i + 1}`,
          cells: [
            visible(String(i + 1)),
            visible(v.label),
            hidden(),
            hidden(),
          ],
        }))
      : [1, 2, 3, 4].map((n) => ({
          id: `row_${n}`,
          cells: [
            visible(String(n)),
            hidden(),
            hidden(),
            hidden(),
          ],
        }));

  return {
    layoutType: 'case_comparison',
    title: payload.title,
    instructionText: `${intro} ${payload.title ? `Tema: ${payload.title}.` : ''}`.trim(),
    headers: [...openExplorationHeadersForDomain(domain)],
    rows,
    workspace: { gridType: 'blank', heightRows: 5 },
  };
}

function buildNumericReachTargetMatrix(payload: ExploraPayload): SimulationMatrixPayload {
  const unit = payload.unitLabel?.trim();
  const scaleHint = unit
    ? `Partiendo de ${payload.initialValue} ${unit}, registra cómo evoluciona hasta ${payload.targetValue}.`
    : `Partiendo de ${payload.initialValue}, registra cómo evoluciona el valor hasta ${payload.targetValue}.`;

  return {
    layoutType: 'variable_tracking',
    title: payload.title,
    instructionText: scaleHint,
    headers: ['Paso', 'Acción', 'Valor resultante', '¿Objetivo alcanzado?'],
    rows: [1, 2, 3, 4].map((n) => ({
      id: `try_${n}`,
      cells: [visible(String(n)), hidden(), hidden(), hidden('sí/no')],
    })),
    workspace: { gridType: 'math_5mm', heightRows: 5 },
  };
}

export function buildFractionDecimalMatrix(topic: string): SimulationMatrixPayload {
  const short = topic.slice(0, 72) || 'Fracciones y decimales';
  return {
    layoutType: 'case_comparison',
    title: `Tabla de equivalencias · ${short}`,
    instructionText:
      'Completa la columna de decimal equivalente para cada fracción. Usa el espacio de cálculos si lo necesitas.',
    headers: ['Ítem', 'Fracción', 'Decimal', '¿Equivalente?'],
    rows: [
      {
        id: 'r1',
        cells: [visible('A'), visible('1/2'), hidden('decimal'), hidden('sí/no')],
      },
      {
        id: 'r2',
        cells: [visible('B'), visible('3/4'), hidden('decimal'), hidden('sí/no')],
      },
      {
        id: 'r3',
        cells: [visible('C'), visible('2/5'), hidden('decimal'), hidden('sí/no')],
      },
      {
        id: 'r4',
        cells: [visible('D'), visible('1/4'), hidden('decimal'), hidden('sí/no')],
      },
    ],
    workspace: { gridType: 'math_5mm', heightRows: 5 },
  };
}

export function buildPercentVariationMatrix(topic: string): SimulationMatrixPayload {
  const short = topic.slice(0, 72) || 'Variación porcentual';
  return {
    layoutType: 'variable_tracking',
    title: `Seguimiento de precios · ${short}`,
    instructionText:
      'Calcula la variación porcentual entre el precio original y el nuevo precio. Registra tu procedimiento abajo.',
    headers: ['Producto', 'Precio original', 'Nuevo precio', 'Variación (%)'],
    rows: [
      {
        id: 'r1',
        cells: [visible('Ítem A'), visible('$40'), visible('$50'), hidden('%')],
      },
      {
        id: 'r2',
        cells: [visible('Ítem B'), visible('$80'), visible('$92'), hidden('%')],
      },
      {
        id: 'r3',
        cells: [visible('Ítem C'), visible('$120'), visible('$138'), hidden('%')],
      },
    ],
    workspace: { gridType: 'math_5mm', heightRows: 6 },
  };
}

export function buildMatrixFromExplora(
  payload: ExploraPayload,
  hints = '',
  subjectTag?: string | null,
): SimulationMatrixPayload {
  if (payload.variant === 'fracciones' || payload.skin === 'fractions') {
    return {
      layoutType: 'case_comparison',
      title: payload.title,
      instructionText:
        'Registra cada paso al construir la fracción objetivo. Anota el valor actual después de cada acción.',
      headers: ['Paso', 'Acción', 'Valor parcial', 'Meta'],
      rows: payload.availableVariables.slice(0, 4).map((v, i) => ({
        id: `step_${i + 1}`,
        cells: [
          visible(String(i + 1)),
          visible(v.label),
          hidden('valor'),
          visible(String(payload.targetValue)),
        ],
      })),
      workspace: { gridType: 'math_5mm', heightRows: 4 },
    };
  }

  if (
    (payload.skin === 'economy' || payload.variant === 'grafico') &&
    isMathOrientedExplora(payload, hints)
  ) {
    return {
      layoutType: 'variable_tracking',
      title: payload.title,
      instructionText:
        'Registra el valor base, el modificador aplicado y el valor final para cada caso.',
      headers: ['Ítem', 'Valor base', 'Modificador', 'Valor final'],
      rows: [
        {
          id: 'r1',
          cells: [visible('Ítem A'), visible('$40'), hidden('%'), hidden('valor')],
        },
        {
          id: 'r2',
          cells: [visible('Ítem B'), visible('$80'), hidden('%'), hidden('valor')],
        },
        {
          id: 'r3',
          cells: [visible('Ítem C'), visible('$120'), hidden('%'), hidden('valor')],
        },
      ],
      workspace: { gridType: 'math_5mm', heightRows: 5 },
    };
  }

  if (isMathOrientedExplora(payload, hints)) {
    return buildNumericReachTargetMatrix(payload);
  }

  return buildOpenExplorationMatrix(payload, subjectTag);
}

export function buildMatrixFromInverseOperations(payload: ExploraPayload): SimulationMatrixPayload {
  const equation =
    payload.displayEquation ??
    (payload.equationLeft && payload.equationRight
      ? `${payload.equationLeft.xBoxes}x + ${payload.equationLeft.constants} = ${payload.equationRight.constants}`
      : 'Ecuación');
  return {
    layoutType: 'variable_tracking',
    title: payload.title,
    instructionText: `Despeja la incógnita paso a paso. Ecuación inicial: ${equation}.`,
    headers: ['Paso', 'Operación inversa', 'Ecuación resultante', '¿x aislada?'],
    rows: payload.availableVariables.slice(0, 4).map((v, i) => ({
      id: `inv_${i + 1}`,
      cells: [
        visible(String(i + 1)),
        visible(v.label),
        hidden('ecuación'),
        hidden('sí/no'),
      ],
    })),
    workspace: { gridType: 'math_5mm', heightRows: 6 },
  };
}

export function buildMatrixFromInflation(payload: InflationMarketPayload): SimulationMatrixPayload {
  return {
    layoutType: 'data_ledger',
    title: payload.title,
    instructionText: `Analiza la evolución del precio de ${payload.productName}. ¿Qué equipo se acerca más al objetivo $${payload.targetPrice}?`,
    headers: ['Equipo', 'Modificador (%)', 'Precio estimado', 'Distancia al objetivo'],
    rows: payload.teams.flatMap((team) =>
      team.modifiers.slice(0, 2).map((mod, i) => ({
        id: `${team.teamId}_${i}`,
        cells: [
          visible(team.label),
          visible(`${mod.percent > 0 ? '+' : ''}${mod.percent}%`),
          hidden('$'),
          hidden('Δ'),
        ],
      })),
    ),
    workspace: { gridType: 'blank', heightRows: 4 },
  };
}

export function buildMatrixForIntent(
  intent: SandboxPrintIntent,
  payload: SandboxPayload,
  topicHint: string,
  subjectTag?: string | null,
): SimulationMatrixPayload {
  const hints = collectSandboxTopicHints(payload, topicHint);

  switch (intent) {
    case 'percent-variation':
      return buildPercentVariationMatrix(hints);
    case 'fraction-decimal-table':
      return buildFractionDecimalMatrix(hints);
    case 'inverse-operations':
      return buildMatrixFromInverseOperations(payload as ExploraPayload);
    case 'inflation':
      return buildMatrixFromInflation(payload as InflationMarketPayload);
    case 'explora-fractions':
      return buildMatrixFromExplora(payload as ExploraPayload, hints, subjectTag);
    case 'numeric-reach-target':
      return buildNumericReachTargetMatrix(payload as ExploraPayload);
    case 'text-composition':
      return buildTextCompositionMatrix(payload as ExploraPayload, hints);
    case 'open-exploration':
    default:
      if (topicLooksLanguage(hints)) {
        return buildTextCompositionMatrix(payload as ExploraPayload, hints);
      }
      return isExploraPayload(payload)
        ? buildOpenExplorationMatrix(payload, subjectTag)
        : buildOpenExplorationMatrix(
            {
              variant: 'balanza',
              title: hints.slice(0, 96) || 'Explora',
              targetValue: 100,
              initialValue: 0,
              availableVariables: [],
            },
            subjectTag,
          );
  }
}

export function resolveSimulationMatrixForSandbox(
  payload: SandboxPayload,
  topicHint = '',
  subjectTag: string | null = null,
): SimulationMatrixPayload {
  const domain = resolveOpenExplorationDomain(subjectTag);
  const explicit =
    payload && typeof payload === 'object' && 'simulationMatrix' in payload
      ? (payload as { simulationMatrix?: unknown }).simulationMatrix
      : null;

  if (explicit) {
    try {
      const validated = validateSimulationMatrixPayload(explicit);
      const hints = collectSandboxTopicHints(payload, topicHint);
      const hasGenericLanguageMatrix =
        topicLooksLanguage(hints) &&
        (matrixHasMathWorkshopHeaders(validated.headers) ||
          validated.rows.some((row) =>
            row.cells.some(
              (cell) =>
                !cell.isHiddenInput &&
                /factor clave|contrapeso|catalizador|factor a|factor b/i.test(cell.value),
            ),
          ));
      const hasCrossSubjectMathScaffold =
        domain !== 'matematica' && matrixHasMathWorkshopHeaders(validated.headers);
      if (!hasGenericLanguageMatrix && !hasCrossSubjectMathScaffold) {
        return enrichSimulationMatrixForSandbox(validated, payload);
      }
    } catch {
      /* reconstruir desde intent */
    }
  }

  const intent = inferSandboxPrintIntent(payload, topicHint);
  return enrichSimulationMatrixForSandbox(
    buildMatrixForIntent(intent, payload, topicHint, subjectTag),
    payload,
  );
}
